import React, { useState, useEffect } from 'react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ChevronDown, ChevronUp, Eye, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import ImagePopup from '@/components/ImagePopup';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import { supabase } from '@/integrations/supabase/client';
import SEO from '@/components/SEO';

const Auth = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [selectedImage, setSelectedImage] = useState<any | null>(null);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  
  const {
    signUp,
    signIn,
    sendPasswordReset,
    user
  } = useSecureAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // List of legitimate email providers
  const legitimateEmailDomains = [
    'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com',
    'aol.com', 'protonmail.com', 'mail.com', 'zoho.com', 'yandex.com',
    'live.com', 'msn.com', 'rediffmail.com', 'tutanota.com', 'fastmail.com',
    'gmx.com', 'mail.ru', 'qq.com', '163.com', 'sina.com'
  ];
  
  const validateEmailDomain = async (email: string): Promise<boolean> => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    
    // Normalize email for consistent checks
    const sanitizedEmail = email.toLowerCase().trim();
    const domain = sanitizedEmail.split('@')[1]?.toLowerCase();
    if (!domain) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    
    // Check if email is on the allowlist (case-insensitive)
    const { data: isAllowed } = await supabase.rpc('is_email_allowed', {
      check_email: sanitizedEmail
    });
    
    // Allow if email is on allowlist OR domain is legitimate
    const isLegitimate = legitimateEmailDomains.includes(domain);
    if (!isAllowed && !isLegitimate) {
      setEmailError('Please use a legitimate email provider (Gmail, Yahoo, Outlook, etc.) or contact admin for access');
      return false;
    }
    
    setEmailError('');
    return true;
  };
  
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    if (newEmail && newEmail.includes('@')) {
      validateEmailDomain(newEmail);
    } else if (emailError) {
      setEmailError('');
    }
  };
  
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
    
    // Check URL mode parameter
    const mode = searchParams.get('mode');
    if (mode === 'reset') {
      setIsResetMode(true);
      setIsPasswordReset(false);
      setIsSignUp(false);
    } else if (mode === 'signup') {
      setIsSignUp(true);
      setIsResetMode(false);
      setIsPasswordReset(false);
    }
  }, [user, navigate, searchParams]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const isValid = await validateEmailDomain(email);
    if (!isValid) {
      return;
    }
    
    setLoading(true);
    try {
      if (isResetMode) {
        // Handle actual password reset
        if (password !== confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        
        if (password.length < 8) {
          toast.error('Password must be at least 8 characters long');
          setLoading(false);
          return;
        }

        const { error } = await supabase.auth.updateUser({
          password: password
        });

        if (error) {
          toast.error(error.message);
        } else {
          toast.success('Password updated successfully!');
          navigate('/dashboard');
        }
      } else if (isPasswordReset) {
        console.log('=== AUTH PAGE: PASSWORD RESET REQUESTED ===');
        console.log('Email:', email);
        console.log('Using sendPasswordReset from SecureAuthContext...');
        
        const { error } = await sendPasswordReset(email);
        
        console.log('sendPasswordReset result:', { error });
        
        if (error) {
          console.error('Password reset error in Auth page:', error);
          toast.error(error.message);
        } else {
          console.log('Password reset successful in Auth page');
          toast.success('Password reset email sent! Check your inbox and click the link to reset your password.');
          setIsPasswordReset(false);
          setEmail(''); // Clear the email field
        }
      } else if (isSignUp) {
        const { error } = await signUp(email, password, fullName);
        if (error) {
          toast.error(error.message);
        } else {
          toast.success('Check your email for the confirmation link!');
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          // Provide specific guidance for common login issues
          if (error.message.includes('Invalid login credentials') || error.message.includes('invalid_credentials')) {
            toast.error('Invalid email or password. Check your credentials or verify your email if you just signed up.');
          } else if (error.message.includes('Email not confirmed')) {
            toast.error('Please check your email and click the confirmation link before signing in.');
          } else {
            toast.error(error.message);
          }
        } else {
          toast.success('Signed in successfully!');
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  const fullPrompt = "The bag should feature a modern pastel Halloween palette (lavender, pastel orange, mint green, soft pink, and black accents). The overall aesthetic should be a mix of spooky and kawaii (cute) — think adorable cartoon ghosts, smiling pumpkins with blush cheeks, sparkly bats, and cute little witches with oversized hats and braids. The design should include fun patterns like stars, candy, spiderwebs with heart centers, and crescent moons. Add glow or glitter effects subtly to make it pop. The bag should look trendy and Instagram-worthy — appealing to both tweens and older teens. Add a catchy and playful phrase like: \"Too Cute to Spook!\" or \"Witch, Please!\" in a stylish, handwritten or bubbly font. Optional: Include a small matching tag for each bag with a name area and mini icon (ghost, bat, or candy).";
  const trimmedPrompt = "The bag should feature a modern pastel Halloween palette with spooky and kawaii aesthetic — think adorable cartoon ghosts, smiling pumpkins, sparkly bats, and cute witches...";
  
  const halloweenImages = [
    {
      id: "1",
      prompt: fullPrompt,
      image_url: "/lovable-uploads/8766898c-ce9c-4ea1-9124-dc54a095b987.png",
      model: "DALL-E 3",
      size: "1024x1024",
      style: "vivid",
      quality: "hd",
      created_at: new Date().toISOString()
    }, {
      id: "2",
      prompt: fullPrompt,
      image_url: "/lovable-uploads/dacbe23f-ad8e-4ab3-84f1-17edffc39a88.png",
      model: "DALL-E 3",
      size: "1024x1024",
      style: "natural",
      quality: "standard",
      created_at: new Date().toISOString()
    }, {
      id: "3",
      prompt: fullPrompt,
      image_url: "/lovable-uploads/00134a88-7297-43e5-a887-11bd50abcdf8.png",
      model: "Midjourney",
      size: "1024x1024",
      style: "anime",
      quality: "hd",
      created_at: new Date().toISOString()
    }, {
      id: "4",
      prompt: fullPrompt,
      image_url: "/lovable-uploads/517f955e-e90d-43c6-87c7-72da7bdb7891.png",
      model: "DALL-E 3",
      size: "1024x1024",
      style: "vivid",
      quality: "hd",
      created_at: new Date().toISOString()
    }, {
      id: "5",
      prompt: fullPrompt,
      image_url: "/lovable-uploads/de9fcde9-fd57-437c-8fa5-91d90f56f09a.png",
      model: "Stable Diffusion",
      size: "1024x1024",
      style: "realistic",
      quality: "high",
      created_at: new Date().toISOString()
    }, {
      id: "6",
      prompt: fullPrompt,
      image_url: "/lovable-uploads/48d4bdc2-32cd-4a3e-b920-39a4efa31c41.png",
      model: "DALL-E 3",
      size: "1024x1024",
      style: "natural",
      quality: "standard",
      created_at: new Date().toISOString()
    }, {
      id: "7",
      prompt: fullPrompt,
      image_url: "/lovable-uploads/7c23f460-c621-400a-9eea-f1151138beca.png",
      model: "Midjourney",
      size: "1024x1024",
      style: "kawaii",
      quality: "hd",
      created_at: new Date().toISOString()
    }, {
      id: "8",
      prompt: fullPrompt,
      image_url: "/lovable-uploads/83e820ef-fdee-4393-93e4-38c89ce3c1d2.png",
      model: "DALL-E 3",
      size: "1024x1024",
      style: "vivid",
      quality: "hd",
      created_at: new Date().toISOString()
    }
  ];
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex relative overflow-hidden">
      <SEO
        title="Sign In | The Bulk Image Generator"
        description="Sign in to access AI-powered bulk image generation. Create stunning visuals for your business."
        url="/auth"
        noindex={true}
      />
      {/* Grid Background Pattern */}
      <div className="absolute inset-0 bg-grid-white/10 bg-[size:30px_30px] sm:bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black_80%)]"></div>
      
      {/* Additional grid overlay for enhanced effect */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-cyan-600/5 via-transparent to-pink-600/5"></div>

      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <div className="w-full max-w-md space-y-8">
          {/* Back to Home */}
          <Link to="/" className="inline-flex items-center text-gray-400 hover:text-white transition-colors text-sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold text-white">
              {isResetMode ? 'Set New Password' : isPasswordReset ? 'Reset Password' : isSignUp ? 'Create Account' : 'Login'}
            </h1>
            <p className="text-gray-400">
              {isResetMode 
                ? 'Enter your new password below'
                : isPasswordReset 
                  ? 'Enter your email to receive a password reset link'
                  : isSignUp 
                    ? 'Join us to start generating amazing images' 
                    : 'Enter your email below to login to your account'
              }
            </p>
          </div>

          {/* Form */}
          <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
            <CardContent className="p-6 space-y-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {isSignUp && !isPasswordReset && !isResetMode && (
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-white">Full Name</Label>
                    <Input 
                      id="fullName" 
                      type="text" 
                      placeholder="John Doe" 
                      value={fullName} 
                      onChange={e => setFullName(e.target.value)} 
                      required 
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-blue-500" 
                    />
                  </div>
                )}
                
                {!isResetMode && (
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white">Email</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      placeholder="name@gmail.com" 
                      value={email} 
                      onChange={handleEmailChange} 
                      required 
                      className={`bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-blue-500 ${emailError ? 'border-red-500' : ''}`} 
                    />
                    {emailError && (
                      <Alert className="bg-red-500/10 border-red-500/20">
                        <AlertCircle className="h-4 w-4 text-red-400" />
                        <AlertDescription className="text-red-400 text-sm">
                          {emailError}
                        </AlertDescription>
                      </Alert>
                    )}
                    <p className="text-xs text-gray-400">
                      Please use a legitimate email provider (Gmail, Yahoo, Outlook, etc.) or an approved email from admin
                    </p>
                  </div>
                )}
                
                {(!isPasswordReset || isResetMode) && (
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-white">
                      {isResetMode ? 'New Password' : 'Password'}
                    </Label>
                    <Input 
                      id="password" 
                      type="password" 
                      placeholder={isResetMode ? "Enter your new password" : "Enter your password"} 
                      value={password} 
                      onChange={e => setPassword(e.target.value)} 
                      required 
                      minLength={8} 
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-blue-500" 
                    />
                    {(isSignUp || isResetMode) && password && (
                      <div className="mt-3">
                        <PasswordStrengthIndicator password={password} />
                      </div>
                    )}
                  </div>
                )}
                
                {isResetMode && (
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-white">Confirm New Password</Label>
                    <Input 
                      id="confirmPassword" 
                      type="password" 
                      placeholder="Confirm your new password" 
                      value={confirmPassword} 
                      onChange={e => setConfirmPassword(e.target.value)} 
                      required 
                      minLength={8} 
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-blue-500" 
                    />
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-white text-black hover:bg-gray-100 font-medium" 
                  disabled={loading || !!emailError}
                >
                  {loading ? 'Loading...' : isResetMode ? 'Update Password' : isPasswordReset ? 'Send Reset Link' : isSignUp ? 'Sign Up with Email' : 'Sign In with Email'}
                </Button>
              </form>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-white/20" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-slate-800 px-2 text-gray-400">Or continue with</span>
                </div>
              </div>

              {/* Social Login Buttons */}
              <div className="space-y-3">
                <div className="relative">
                  <Button type="button" variant="outline" className="w-full h-11 bg-white/5 border-white/20 text-white hover:bg-white/10" onClick={() => toast.info('Google login coming soon!')}>
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </Button>
                  <Badge className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full text-[10px]">
                    Coming Soon
                  </Badge>
                </div>
              </div>

              {/* Terms */}
              <p className="text-xs text-gray-400 text-center">
                By continuing, you agree to our{' '}
                <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline">
                  Privacy Policy
                </Link>
                .
              </p>
              
              {/* Switch between login/signup/reset */}
              {!isResetMode && (
                <div className="text-center space-y-2">
                  {!isPasswordReset && (
                    <button 
                      type="button" 
                      onClick={() => setIsSignUp(!isSignUp)} 
                      className="text-blue-400 hover:text-blue-300 text-sm underline block w-full"
                    >
                      {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
                    </button>
                  )}
                  
                  {!isSignUp && (
                    <button 
                      type="button" 
                      onClick={() => setIsPasswordReset(!isPasswordReset)} 
                      className="text-blue-400 hover:text-blue-300 text-sm underline block w-full"
                    >
                      {isPasswordReset ? 'Back to login' : 'Forgot your password?'}
                    </button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Testimonial/Branding */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-blue-600/20 to-purple-600/20 items-center justify-center p-6 overflow-hidden relative z-10">
        <div className="max-w-lg text-center space-y-3 h-full flex flex-col justify-center">
          <div className="flex items-center justify-center space-x-2 mb-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">BG</span>
            </div>
            <span className="text-xl font-bold text-white">The Bulk Image Generator</span>
          </div>
          
          {/* Halloween Bag Images Grid - 4x2 layout */}
          <div className="grid grid-cols-4 gap-2 mb-3">
            {halloweenImages.map((image, index) => (
              <ImagePopup
                key={index}
                image={image}
                onCopySettings={() => toast.success("Settings copied to clipboard!")}
              >
                <div className="relative group cursor-pointer">
                  <img 
                    src={image.image_url} 
                    alt={`Halloween bag design ${index + 1}`} 
                    className="w-full h-24 object-cover rounded-md shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" 
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iIzM3NDE0OSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5Q0E0QUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+';
                    }}
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-md flex items-center justify-center">
                    <Eye className="w-4 h-4 text-white" />
                  </div>
                </div>
              </ImagePopup>
            ))}
          </div>

          {/* Design Brief - Expandable */}
          <div className="text-sm text-gray-300 leading-relaxed bg-white/5 p-3 rounded-lg border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-white">Design Brief</h3>
              <Button variant="ghost" size="sm" onClick={() => setIsPromptExpanded(!isPromptExpanded)} className="text-gray-400 hover:text-white p-1 h-auto">
                {isPromptExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
            <p className={`transition-all duration-300 ${isPromptExpanded ? 'max-h-none' : 'max-h-16 overflow-hidden'}`}>
              {isPromptExpanded ? fullPrompt : trimmedPrompt}
            </p>
          </div>

          <blockquote className="text-base text-gray-300 leading-relaxed">
            "This tool is a game-changer for anyone working with AI image generation. 
            Highly recommended!"
          </blockquote>
          
          <div className="text-gray-400">
            <p className="font-semibold">Alex Johnson</p>
            <p className="text-xs">Content Creator</p>
          </div>

          {/* Floating elements for visual interest */}
          <div className="absolute top-1/4 right-1/4 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
